import { useEffect, useRef, useState } from "react";
import { ExternalLink, Menu, X } from "lucide-react";
import { Link, NavLink } from "react-router-dom";
import { isExternalTarget, resolveLink } from "../config/links";
import { navigation, routes } from "../config/routes";
import { sharedContent } from "../content/siteContent";

export function SmartLink({ target, className, children, onClick }) {
  const href = resolveLink(target);
  if (isExternalTarget(target)) {
    return (
      <a className={className} href={href} target="_blank" rel="noopener noreferrer" onClick={onClick}>
        <span>{children}</span>
        <ExternalLink aria-hidden="true" size={15} strokeWidth={1.8} />
      </a>
    );
  }
  return <Link className={className} to={href} onClick={onClick}>{children}</Link>;
}

export function SiteHeader() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const closeButtonRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    document.body.dataset.menuOpen = "true";
    closeButtonRef.current?.focus();
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      delete document.body.dataset.menuOpen;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const closeMenu = () => setOpen(false);

  return (
    <header className={`site-header${scrolled ? " is-scrolled" : ""}`}>
      <div className="header-inner">
        <Link className="brand-link" to={routes.home} aria-label="LevelUp360 home" onClick={closeMenu}>
          <img src="/assets/brand/levelup360-white.svg" alt="LevelUp360" width="210" height="44" />
        </Link>
        <button className="menu-toggle" type="button" aria-expanded={open} aria-controls="site-navigation" onClick={() => setOpen((value) => !value)} ref={open ? closeButtonRef : null}>
          {open ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
          <span className="sr-only">{open ? "Close menu" : "Open menu"}</span>
        </button>
        <nav id="site-navigation" className={`site-navigation${open ? " is-open" : ""}`} aria-label="Primary navigation">
          <ul className="nav-list">
            {navigation.map((item) => (
              <li key={item.path}>
                <NavLink to={item.path} onClick={closeMenu} className={({ isActive }) => isActive ? "nav-link is-active" : "nav-link"}>
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
          <SmartLink className="button button-primary header-cta" target="bookCall" onClick={closeMenu}>Book A Call</SmartLink>
        </nav>
      </div>
    </header>
  );
}

export function SiteFooter() {
  return (
    <footer className="site-footer">
      <div className="footer-grid">
        <div className="footer-brand">
          <img src="/assets/brand/levelup360-white.svg" alt="LevelUp360" width="210" height="44" loading="lazy" />
          <p>{sharedContent.footer.description}</p>
        </div>
        <nav aria-label="Footer navigation">
          <h2 className="footer-heading">Explore</h2>
          <ul className="footer-links">
            {navigation.slice(1).map((item) => <li key={item.path}><Link to={item.path}>{item.label}</Link></li>)}
          </ul>
        </nav>
        <div className="footer-actions">
          <h2 className="footer-heading">Continue</h2>
          <SmartLink target="bookCall">Book A Call</SmartLink>
          <SmartLink target="newsletter">Newsletter</SmartLink>
          <SmartLink target="manuelLinkedIn">Manuel On LinkedIn</SmartLink>
          <Link to={routes.privacy}>Privacy Policy</Link>
          <Link to={routes.terms}>Terms And Conditions</Link>
        </div>
      </div>
      <div className="footer-bottom">
        <p>&copy; 2026 LevelUp360. All rights reserved.</p>
      </div>
    </footer>
  );
}
